// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Human-readable names for assets the twin publishes with opaque identifiers.
 *
 * Extracted verbatim from `kgOrdering.ts` when the type-grouped Asset Topology
 * was replaced by Power Flow. The rest of that module was `resolveGroups` —
 * grouping assets by entity type, which is precisely the axis Power Flow moves
 * away from — but the label derivation is orthogonal to it and is what makes a
 * chip readable when `attributes.label` is a bare GUID.
 */

import type { Entity } from '../types/digitalTwin';
import { getEntityCode } from './entityHelpers';
import { isKnownMeasurement } from './ontologyAccess';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_BLOB_RE = /^[0-9a-f]{16,}$/i;
const TRAILING_TOKEN_RE = /(\d+[A-Za-z]?)$/;

/**
 * Whether a string is an identifier rather than a name — a UUID or a long hex
 * blob. Exported because "never show one of these to a reader" is the rule the
 * label helpers below exist to enforce.
 */
export const isOpaqueIdentifier = (value: string): boolean =>
  UUID_RE.test(value) || HEX_BLOB_RE.test(value);

/**
 * The site's own identifier for an asset — `GEN-1-A`, not
 * `Diesel-Generator-Block-A`.
 *
 * This is the name on the nameplate, in the one-line drawing and over the
 * radio, so it is what every screen shows. `attributes.label` is a
 * description, not an identity: two assets can share one, it is written for
 * prose rather than for pointing at a thing, and it left the interface calling
 * the same generator by a different name than the operator standing in front
 * of it. The label is still published in the detail panel's Entity info.
 *
 * `undefined` when the twin publishes no upstream id, or publishes a GUID as
 * one — then the label rules below still apply.
 *
 * Also `undefined` for a measurement point, where the upstream id is a point
 * name rather than an asset tag: every CRAH on the site publishes
 * `return_air_temperature`, so it names a *kind* of reading and not the thing
 * taking it. Its label already carries the parent — `CRAH-1-A Return Air
 * Temperature Sensor` — and is the only string of the two that identifies one
 * point. The equipment that owns them keeps its upstream id.
 */
export const upstreamIdOf = (entity: Entity): string | undefined => {
  if (isKnownMeasurement(entity.type)) return undefined;
  const value = entity.attributes?.upstreamId;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed && !isOpaqueIdentifier(trimmed) ? trimmed : undefined;
};

/** Trailing number (with optional letter), e.g. "…/gen-12A" → "12A". */
const trailingToken = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const match = value.match(TRAILING_TOKEN_RE);
  return match ? match[1] : undefined;
};

/**
 * Chip label for an asset. Chips are ~104px wide (~12 characters), so the goal
 * is a short human-recognisable name.
 *
 * The upstream id is always preferred — it is the asset's identity on site and
 * it already fits a chip. Only when there is none does the published `label`
 * stand in, and then only when it carries information for a human (a bare GUID
 * does not), where a type code plus a disambiguating suffix at least says what
 * the thing is.
 */
export const deriveUnitShortLabel = (entity: Entity): string => {
  const upstream = upstreamIdOf(entity);
  if (upstream) return upstream;

  const label = entity.attributes?.label?.trim();
  const labelIsOpaque = !label || isOpaqueIdentifier(label);
  if (!labelIsOpaque) return label as string;

  // An opaque label is not mined for a suffix — the tail of a GUID is just more
  // GUID. Only a usable label (or the id) can contribute a disambiguator.
  const code = getEntityCode(entity.type);
  const suffix = trailingToken(entity.id) ?? entity.id.slice(-4);
  return `${code}-${suffix}`;
};

/**
 * Full label for the panel and the accessible name: the name in full, with no
 * ellipsis and no truncation, since neither is width-constrained.
 *
 * Identical to the short label wherever an upstream id exists, which is the
 * common case — an upstream id is already short, and a panel heading that
 * disagrees with the chip the reader clicked is worse than a repeated one. The
 * published `label` only widens the name where there is no upstream id to show.
 */
export const deriveUnitFullLabel = (entity: Entity): string => {
  if (upstreamIdOf(entity)) return deriveUnitShortLabel(entity);
  const label = entity.attributes?.label?.trim();
  return label && !isOpaqueIdentifier(label) ? label : deriveUnitShortLabel(entity);
};

/**
 * The name for an asset the caller holds only an id for.
 *
 * The one place the "a reader never sees a GUID" rule lives, for the many
 * screens that resolve an id against a payload and render the result. Every one
 * of them used to hand-roll `entity?.attributes?.label ?? id`, which leaks the
 * id twice over: when the entity has no label, and when its label *is* an id.
 *
 * An id that resolves to nothing is the one case that still renders as itself.
 * The asset is not in the payload, so there is no name to show, and inventing
 * one would be worse than printing the only identifier that exists — the
 * screens that can hit this (the detail panel, the dangling-connection notice)
 * say so in words alongside it.
 */
export const assetName = (entity: Entity | undefined, id: string): string =>
  entity ? deriveUnitShortLabel(entity) : id;
